import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

/**
 * Password input with a built-in show/hide (eye) toggle.
 *
 * Drop-in replacement for `<input type="password" .../>`. Spreads all native
 * input props and forwards the ref, so it works with both React Hook Form
 * `register(...)` and controlled `value`/`onChange` inputs. Do NOT pass `type`
 * — it is managed internally by the toggle.
 */
type PasswordInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>;

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className = '', ...rest }, ref) => {
    const [show, setShow] = useState(false);
    return (
      <div className="relative">
        <input
          {...rest}
          ref={ref}
          type={show ? 'text' : 'password'}
          // Reserve room on the right so typed text never sits under the icon.
          className={`${className} pr-10`.trim()}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow((s) => !s)}
          aria-label={show ? 'Hide password' : 'Show password'}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
        >
          {show ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
        </button>
      </div>
    );
  }
);
PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
